/**
 * Workspace — Agency-tier dashboard combining three concerns:
 *   1. Team members (invite up to 5 seats, view/remove)
 *   2. Scheduled re-audits (weekly/monthly per URL)
 *   3. Webhook URL (single POST endpoint on audit completion)
 *
 * The whole page gates on the Agency plan. Lower tiers see a friendly
 * upgrade prompt with the specific Agency-only features listed.
 */
import { useEffect, useState } from "react";
import { ArrowSquareOut, CalendarBlank, Copy, Plus, Trash, Users, WebhooksLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { fmtDate, shortUrl } from "@/lib/score-utils";

export default function WorkspacePage() {
  const [ws, setWs] = useState(null);
  const [hooks, setHooks] = useState(null);
  const [scheds, setScheds] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [w, h, s] = await Promise.all([
        api.get("/workspace"),
        api.get("/me/webhook"),
        api.get("/schedules"),
      ]);
      setWs(w.data); setHooks(h.data); setScheds(s.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load workspace");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <AppShell><div className="text-ink-muted">Loading…</div></AppShell>;

  const agencyAllowed = ws?.allowed && hooks?.allowed && scheds?.allowed;

  return (
    <AppShell>
      <div className="label-overline mb-2">Agency</div>
      <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter leading-none mb-2">
        Workspace
      </h1>
      <p className="text-sm text-ink-muted mb-10 max-w-2xl">
        Team seats, scheduled re-audits, and audit-completion webhooks —
        the three Agency-only superpowers, all in one place.
      </p>

      {!agencyAllowed && (
        <div className="surface-card hard-shadow-sm p-6 lg:p-8 max-w-3xl mb-8" data-testid="agency-upgrade-card">
          <div className="label-overline mb-2 text-warning">Agency plan required</div>
          <h2 className="font-display text-2xl font-bold tracking-tight mb-2">
            Unlock teams, schedules, and webhooks.
          </h2>
          <p className="text-sm text-ink-muted mb-5">
            These features ship with the Agency tier — unlimited audits,
            five team seats, weekly/monthly re-audits, and outbound
            webhooks on every completion.
          </p>
          <Link to="/app/billing" data-testid="workspace-upgrade-cta"
            className="inline-flex items-center gap-2 bg-ink text-white px-5 py-2.5 rounded-sm font-semibold hover:bg-brand transition-colors">
            Upgrade to Agency <ArrowSquareOut size={14} />
          </Link>
        </div>
      )}

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 mb-8">
        <WorkspaceTeam ws={ws} reload={load} disabled={!agencyAllowed} />
        <WebhookCard hooks={hooks} reload={load} disabled={!agencyAllowed} />
      </div>

      <SchedulesCard scheds={scheds} reload={load} disabled={!agencyAllowed} />
    </AppShell>
  );
}

/* ------------------------------------------------------------------ */
/* Team / Workspace                                                    */
/* ------------------------------------------------------------------ */
function WorkspaceTeam({ ws, reload, disabled }) {
  const [name, setName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const createWs = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/workspace", { name });
      toast.success("Workspace created");
      setName("");
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/workspace/invites", { email: inviteEmail });
      toast.success(`Invite sent to ${inviteEmail}`);
      setInviteEmail("");
      navigator.clipboard?.writeText(
        `${window.location.origin}/invite?token=${r.data.token}`
      ).catch(() => {});
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const revokeInvite = async (id) => {
    try {
      await api.delete(`/workspace/invites/${id}`);
      toast.success("Invite revoked");
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const removeMember = async (uid) => {
    if (!confirm("Remove this teammate from the workspace?")) return;
    try {
      await api.delete(`/workspace/members/${uid}`);
      toast.success("Member removed");
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const copyInviteLink = (token) => {
    navigator.clipboard?.writeText(`${window.location.origin}/invite?token=${token}`);
    toast.success("Invite link copied");
  };

  return (
    <div className={`surface-card p-6 lg:p-8 ${disabled ? "opacity-60 pointer-events-none" : ""}`} data-testid="workspace-team-card">
      <div className="flex items-center gap-2 mb-2">
        <Users size={20} className="text-brand" weight="duotone" />
        <div className="label-overline">Team seats</div>
      </div>
      <h2 className="font-display text-2xl font-bold tracking-tight mb-1">Members</h2>

      {!ws?.workspace ? (
        <>
          <p className="text-sm text-ink-muted mb-4">
            Create your workspace to start inviting up to{" "}
            <span className="font-semibold text-ink">5 teammates</span> who share your Agency benefits.
          </p>
          <form onSubmit={createWs} className="flex gap-2">
            <input
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Workspace name (e.g. Yellow Thinking)"
              data-testid="workspace-name-input"
              className="flex-1 bg-white border border-ink rounded-sm px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brand"
              required minLength={2}
            />
            <button type="submit" disabled={busy} data-testid="workspace-create-btn"
              className="bg-ink text-white px-4 py-2 rounded-sm font-semibold hover:bg-brand transition-colors text-sm disabled:opacity-50">
              Create
            </button>
          </form>
        </>
      ) : (
        <>
          <div className="text-sm text-ink-muted mb-4">
            <span className="font-semibold text-ink">{ws.workspace.name}</span>
            {" — "}{ws.members.length}/{ws.workspace.max_seats} seats used
            {ws.invites.length > 0 && ` · ${ws.invites.length} pending`}
          </div>

          {/* Members table */}
          <div className="border border-border rounded-sm overflow-hidden mb-5">
            {ws.members.map((m) => (
              <div key={m.id} className="flex items-center justify-between px-4 py-2.5 border-b border-border last:border-0 text-sm">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">
                    {m.name}
                    {m.is_owner && <span className="ml-2 mono text-[10px] tracking-wider px-1.5 py-0.5 bg-brand text-white rounded-sm">OWNER</span>}
                  </div>
                  <div className="text-xs text-ink-muted truncate">{m.email} · joined {fmtDate(m.joined_at)}</div>
                </div>
                {ws.is_owner && !m.is_owner && (
                  <button onClick={() => removeMember(m.id)}
                    className="text-critical hover:bg-critical/10 p-1.5 rounded-sm transition-colors"
                    data-testid={`remove-member-${m.id}`}>
                    <Trash size={14} />
                  </button>
                )}
              </div>
            ))}
            {ws.invites.map((i) => (
              <div key={i.id} className="flex items-center justify-between px-4 py-2.5 border-b border-border last:border-0 text-sm bg-warning/5">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate flex items-center gap-2">
                    <span>{i.email}</span>
                    <span className="mono text-[10px] tracking-wider px-1.5 py-0.5 border border-warning text-warning rounded-sm">PENDING</span>
                  </div>
                  <div className="text-xs text-ink-muted">invited {fmtDate(i.created_at)} · expires {fmtDate(i.expires_at)}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => copyInviteLink(i.token)}
                    className="text-ink-muted hover:text-ink p-1.5 rounded-sm" title="Copy invite link">
                    <Copy size={14} />
                  </button>
                  {ws.is_owner && (
                    <button onClick={() => revokeInvite(i.id)}
                      className="text-critical hover:bg-critical/10 p-1.5 rounded-sm transition-colors">
                      <Trash size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Invite form */}
          {ws.is_owner && (ws.members.length + ws.invites.length < ws.workspace.max_seats) && (
            <form onSubmit={invite} className="flex gap-2">
              <input
                type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="teammate@yourdomain.com"
                data-testid="workspace-invite-input"
                className="flex-1 bg-white border border-ink rounded-sm px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brand"
                required
              />
              <button type="submit" disabled={busy}
                data-testid="workspace-invite-btn"
                className="inline-flex items-center gap-1.5 bg-ink text-white px-4 py-2 rounded-sm font-semibold hover:bg-brand transition-colors text-sm disabled:opacity-50">
                <Plus size={14} /> Invite
              </button>
            </form>
          )}
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Webhook                                                            */
/* ------------------------------------------------------------------ */
function WebhookCard({ hooks, reload, disabled }) {
  const [url, setUrl] = useState(hooks?.webhook_url || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setUrl(hooks?.webhook_url || ""); }, [hooks?.webhook_url]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/me/webhook", { webhook_url: url });
      toast.success(url ? "Webhook saved" : "Webhook cleared");
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className={`surface-card p-6 lg:p-8 ${disabled ? "opacity-60 pointer-events-none" : ""}`} data-testid="webhook-card">
      <div className="flex items-center gap-2 mb-2">
        <WebhooksLogo size={20} className="text-brand" weight="duotone" />
        <div className="label-overline">Webhook</div>
      </div>
      <h2 className="font-display text-2xl font-bold tracking-tight mb-1">Audit completion</h2>
      <p className="text-sm text-ink-muted mb-5">
        We&apos;ll POST a JSON payload to this URL when any of your
        audits finishes:
        <span className="block mono text-[11px] mt-2 px-3 py-2 bg-secondary border border-border rounded-sm break-all">
          {`{ event, audit_id, url, status, scores }`}
        </span>
      </p>
      <form onSubmit={save} className="space-y-3">
        <input
          type="url" value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="https://your-app.com/hooks/auditor"
          data-testid="webhook-url-input"
          className="w-full bg-white border border-ink rounded-sm px-4 py-2.5 mono text-sm outline-none focus:ring-2 focus:ring-brand"
        />
        <button type="submit" disabled={busy} data-testid="webhook-save-btn"
          className="bg-ink text-white px-4 py-2 rounded-sm font-semibold hover:bg-brand transition-colors text-sm disabled:opacity-50">
          {busy ? "Saving…" : url ? "Save webhook" : "Clear webhook"}
        </button>
      </form>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Schedules                                                          */
/* ------------------------------------------------------------------ */
function SchedulesCard({ scheds, reload, disabled }) {
  const [url, setUrl] = useState("");
  const [freq, setFreq] = useState("weekly");
  const [busy, setBusy] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/schedules", { url, frequency: freq });
      toast.success("Schedule created");
      setUrl("");
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const toggle = async (id, active) => {
    try { await api.patch(`/schedules/${id}`, { active }); reload(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const setFrequency = async (id, frequency) => {
    try { await api.patch(`/schedules/${id}`, { frequency }); reload(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this schedule?")) return;
    try { await api.delete(`/schedules/${id}`); reload(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className={`surface-card p-6 lg:p-8 ${disabled ? "opacity-60 pointer-events-none" : ""}`} data-testid="schedules-card">
      <div className="flex items-center gap-2 mb-2">
        <CalendarBlank size={20} className="text-brand" weight="duotone" />
        <div className="label-overline">Scheduled re-audits</div>
      </div>
      <h2 className="font-display text-2xl font-bold tracking-tight mb-1">Watchlist</h2>
      <p className="text-sm text-ink-muted mb-5 max-w-2xl">
        Auditor will re-run any URL you pin here on a weekly or monthly
        cadence and email you a short diff whenever the overall score
        moves by 5 points or more.
      </p>

      <form onSubmit={add} className="flex flex-wrap gap-2 mb-6">
        <input
          type="url" value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="https://yoursite.com"
          data-testid="schedule-url-input"
          className="flex-1 min-w-[260px] bg-white border border-ink rounded-sm px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brand mono"
          required
        />
        <select
          value={freq} onChange={(e) => setFreq(e.target.value)}
          data-testid="schedule-freq-input"
          className="bg-white border border-ink rounded-sm px-3 py-2.5 text-sm">
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
        </select>
        <button type="submit" disabled={busy} data-testid="schedule-add-btn"
          className="inline-flex items-center gap-1.5 bg-ink text-white px-4 py-2 rounded-sm font-semibold hover:bg-brand transition-colors text-sm disabled:opacity-50">
          <Plus size={14} /> Add
        </button>
      </form>

      {scheds?.schedules?.length ? (
        <div className="border border-border rounded-sm overflow-hidden">
          <div className="grid grid-cols-[2fr_1fr_1fr_0.7fr_auto] bg-secondary border-b border-border label-overline text-[10px]">
            <div className="px-4 py-2.5">URL</div>
            <div className="px-4 py-2.5">Frequency</div>
            <div className="px-4 py-2.5">Next run</div>
            <div className="px-4 py-2.5">Score</div>
            <div className="px-4 py-2.5 text-right">Active</div>
          </div>
          {scheds.schedules.map((s) => (
            <div key={s.id} className="grid grid-cols-[2fr_1fr_1fr_0.7fr_auto] items-center border-b border-border last:border-0 text-sm">
              <div className="px-4 py-3 mono text-xs break-all">{shortUrl(s.url)}</div>
              <div className="px-4 py-3">
                <select value={s.frequency} onChange={(e) => setFrequency(s.id, e.target.value)}
                  data-testid={`schedule-freq-${s.id}`}
                  className="bg-white border border-border rounded-sm px-2 py-1 text-xs">
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div className="px-4 py-3 mono text-xs text-ink-muted">{fmtDate(s.next_run_at)}</div>
              <div className="px-4 py-3 num-display font-bold tabular-nums">{s.last_score ?? "—"}</div>
              <div className="px-4 py-3 text-right flex items-center gap-2 justify-end">
                <label className="cursor-pointer flex items-center gap-1.5">
                  <input type="checkbox" checked={s.active} onChange={(e) => toggle(s.id, e.target.checked)}
                    data-testid={`schedule-toggle-${s.id}`} />
                  <span className="text-xs mono text-ink-muted">{s.active ? "ON" : "OFF"}</span>
                </label>
                <button onClick={() => remove(s.id)} data-testid={`schedule-delete-${s.id}`}
                  className="text-critical hover:bg-critical/10 p-1.5 rounded-sm transition-colors">
                  <Trash size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-ink-muted border border-dashed border-border rounded-sm px-4 py-6 text-center">
          No scheduled audits yet. Add one above to start monitoring.
        </div>
      )}
    </div>
  );
}
